import { useEffect, useRef } from "react"
import maplibregl, { type LngLatBoundsLike } from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"

import { debounce } from "../lib/debounce"
import type { Bbox, ListAutomat } from "../types"

export type MapClickPoint = { lat: number; lon: number }

type Props = {
  automaty: ListAutomat[]
  onBboxChange: (b: Bbox) => void
  onPinClick: (id: number) => void
  onMapClick: (p: MapClickPoint) => void
  pickMode: boolean
  pendingPin: MapClickPoint | null
  flyTo?: { lat: number; lon: number; zoom: number }
}

const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY
const STYLE_URL = MAPTILER_KEY
  ? `https://api.maptiler.com/maps/streets/style.json?key=${MAPTILER_KEY}`
  : "https://tiles.openfreemap.org/styles/liberty"

const SRC = "automaty-src"
const LAYER_CLUSTER = "automaty-clusters"
const LAYER_CLUSTER_COUNT = "automaty-cluster-count"
const LAYER_PIN = "automaty-pin"
const LAYER_PENDING = "automaty-pending-pin"

export function MapView({
  automaty,
  onBboxChange,
  onPinClick,
  onMapClick,
  pickMode,
  pendingPin,
  flyTo,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: [19.4, 52.0],
      zoom: 5.8,
      minZoom: 4,
      maxBounds: [
        [13, 48],
        [25, 55.5],
      ] as LngLatBoundsLike,
      attributionControl: false,
    })
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right")
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right")

    const emitBbox = () => {
      const b = map.getBounds()
      onBboxChange([b.getSouth(), b.getWest(), b.getNorth(), b.getEast()])
    }
    const emitDebounced = debounce(emitBbox, 300)

    map.on("load", () => {
      map.addSource(SRC, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: true,
        clusterMaxZoom: 11,
        clusterRadius: 48,
      })
      map.addSource("pending", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      })
      map.addLayer({
        id: LAYER_CLUSTER,
        type: "circle",
        source: SRC,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#0d7b3e",
          "circle-radius": ["step", ["get", "point_count"], 16, 25, 22, 100, 28],
          "circle-stroke-color": "#fff",
          "circle-stroke-width": 2,
        },
      })
      map.addLayer({
        id: LAYER_CLUSTER_COUNT,
        type: "symbol",
        source: SRC,
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-size": 12,
          "text-font": ["Noto Sans Regular"],
        },
        paint: { "text-color": "#fff" },
      })
      map.addLayer({
        id: LAYER_PIN,
        type: "circle",
        source: SRC,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": 9,
          "circle-color": [
            "match",
            ["get", "status"],
            "broken",
            "#c53030",
            "#2f855a",
          ],
          "circle-stroke-color": "#fff",
          "circle-stroke-width": 2,
        },
      })
      map.addLayer({
        id: LAYER_PENDING,
        type: "circle",
        source: "pending",
        paint: {
          "circle-radius": 10,
          "circle-color": "#fff",
          "circle-stroke-color": "#0d7b3e",
          "circle-stroke-width": 3,
        },
      })

      emitBbox()
    })

    map.on("click", LAYER_CLUSTER, (e) => {
      const feat = e.features?.[0]
      if (!feat) return
      const src = map.getSource(SRC) as maplibregl.GeoJSONSource
      const clusterId = (feat.properties as { cluster_id?: number }).cluster_id
      if (clusterId === undefined) return
      src.getClusterExpansionZoom(clusterId).then((z) => {
        const geom = feat.geometry as GeoJSON.Point
        map.easeTo({ center: geom.coordinates as [number, number], zoom: z })
      })
    })
    map.on("click", LAYER_PIN, (e) => {
      const feat = e.features?.[0]
      const id = feat?.properties?.id
      if (typeof id === "number") onPinClick(id)
    })
    map.on("click", (e) => {
      // Only trigger plain-map click if no feature under cursor.
      const hit = map.queryRenderedFeatures(e.point, {
        layers: [LAYER_PIN, LAYER_CLUSTER],
      })
      if (hit.length > 0) return
      onMapClick({ lat: e.lngLat.lat, lon: e.lngLat.lng })
    })
    map.on("moveend", emitDebounced)
    map.on("zoomend", emitDebounced)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update source whenever data changes.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    const src = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined
    if (!src) return
    src.setData({
      type: "FeatureCollection",
      features: automaty.map((a) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [a.lon, a.lat] },
        properties: { id: a.id, status: a.current_status, chain: a.chain ?? "" },
      })),
    })
  }, [automaty])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    const src = map.getSource("pending") as maplibregl.GeoJSONSource | undefined
    if (!src) return
    src.setData({
      type: "FeatureCollection",
      features: pendingPin
        ? [
            {
              type: "Feature",
              geometry: { type: "Point", coordinates: [pendingPin.lon, pendingPin.lat] },
              properties: {},
            },
          ]
        : [],
    })
  }, [pendingPin])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const container = map.getContainer()
    container.style.cursor = pickMode ? "crosshair" : ""
  }, [pickMode])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !flyTo) return
    map.flyTo({ center: [flyTo.lon, flyTo.lat], zoom: flyTo.zoom, essential: true })
  }, [flyTo])

  return <div id="map" ref={containerRef} />
}
